import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import MarketingLinks from '../components/MarketingLinks';
import Toast from '../components/Toast';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function VisitorActivityPage() {
  const navigate = useNavigate();
  const [calendar, setCalendar] = useState({});
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'all'
  const [toast, setToast] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState('lastWeek');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    document.title = 'Visitor Activity - Design Store Admin';
    if (viewMode === 'calendar') {
      fetchCalendar();
    } else {
      fetchAllSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCalendar/fetchAllSessions run when view changes
  }, [currentMonth, viewMode]);

  const fetchCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get date range for current month (using UTC to match backend)
      const year = currentMonth.getUTCFullYear();
      const month = currentMonth.getUTCMonth();
      const startDate = new Date(Date.UTC(year, month, 1));
      const endDate = new Date(Date.UTC(year, month + 1, 0)); // Last day of month
      
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      
      const response = await authAxios.get(`${API_BASE_URL}/admin/visitor-sessions/calendar`, {
        params: {
          startDate: startDateStr,
          endDate: endDateStr
        }
      });
      
      if (response.data.success) {
        setCalendar(response.data.data);
      } else {
        setError('Failed to fetch visitor calendar');
      }
    } catch (err) {
      console.error('Error fetching visitor calendar:', err);
      setError(err.response?.data?.error || 'Failed to fetch visitor calendar');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForAPI = (date) => {
    // Validate date is valid
    if (!date || isNaN(date.getTime())) {
      throw new Error('Invalid date provided to formatDateForAPI');
    }
    // Use UTC to match backend file storage
    const year = date.getUTCFullYear();
    const monthNum = date.getUTCMonth() + 1;
    const dayNum = date.getUTCDate();
    
    // Validate the numeric values before formatting
    if (isNaN(year) || isNaN(monthNum) || isNaN(dayNum) || year < 1900 || year > 2100 || monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
      throw new Error(`Invalid date values: year=${year}, month=${monthNum}, day=${dayNum}`);
    }
    
    const month = String(monthNum).padStart(2, '0');
    const day = String(dayNum).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  const fetchAllSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get last 30 days by default - use UTC to match backend
      const now = new Date();
      const todayYear = now.getUTCFullYear();
      const todayMonth = now.getUTCMonth();
      const todayDay = now.getUTCDate();
      
      // Calculate end date (today)
      const endDate = new Date(Date.UTC(todayYear, todayMonth, todayDay));
      
      // Calculate start date (30 days ago) - handle month/year rollover
      let startYear = todayYear;
      let startMonth = todayMonth;
      let startDay = todayDay - 30;
      
      // Handle day rollover
      while (startDay <= 0) {
        startMonth--;
        if (startMonth < 0) {
          startMonth = 11;
          startYear--;
        }
        const daysInMonth = new Date(Date.UTC(startYear, startMonth + 1, 0)).getUTCDate();
        startDay += daysInMonth;
      }
      
      const startDate = new Date(Date.UTC(startYear, startMonth, startDay));
      
      // Validate dates before formatting
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        setError('Invalid date range calculated');
        return;
      }
      
      let startDateStr, endDateStr;
      try {
        startDateStr = formatDateForAPI(startDate);
        endDateStr = formatDateForAPI(endDate);
        
        // Validate formatted strings
        if (!startDateStr || !endDateStr || startDateStr.includes('NaN') || endDateStr.includes('NaN')) {
          setError('Failed to format dates correctly');
          return;
        }
      } catch (err) {
        console.error('Error formatting dates for API:', err);
        setError('Failed to format dates. Please try again.');
        return;
      }
      
      const response = await authAxios.get(`${API_BASE_URL}/admin/visitor-sessions/all`, {
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          limit: 1000
        }
      });
      
      if (response.data.success) {
        setAllSessions(response.data.data);
      } else {
        setError('Failed to fetch all sessions');
      }
    } catch (err) {
      console.error('Error fetching all sessions:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch all sessions';
      console.error('Full error response:', err.response?.data);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const handleDateClick = (date) => {
    const dateStr = formatDateForAPI(date);
    navigate(`/admin/visitor-activity/${dateStr}`);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  const getDateRange = (rangeType) => {
    const today = new Date();
    const todayYear = today.getUTCFullYear();
    const todayMonth = today.getUTCMonth();
    const todayDay = today.getUTCDate();
    const todayUTC = new Date(Date.UTC(todayYear, todayMonth, todayDay));
    let startDate, endDate;

    switch (rangeType) {
      case 'lastWeek':
        // Calculate 7 days ago - handle day rollover
        let weekAgoYear = todayYear;
        let weekAgoMonth = todayMonth;
        let weekAgoDay = todayDay - 7;
        
        if (weekAgoDay <= 0) {
          weekAgoMonth--;
          if (weekAgoMonth < 0) {
            weekAgoMonth = 11;
            weekAgoYear--;
          }
          const daysInMonth = new Date(Date.UTC(weekAgoYear, weekAgoMonth + 1, 0)).getUTCDate();
          weekAgoDay += daysInMonth;
        }
        
        startDate = new Date(Date.UTC(weekAgoYear, weekAgoMonth, weekAgoDay));
        endDate = new Date(todayUTC);
        break;
      case 'lastMonth':
        // Calculate 1 month ago - handle month rollover properly
        let monthAgoYear = todayYear;
        let monthAgoMonth = todayMonth - 1;
        if (monthAgoMonth < 0) {
          monthAgoMonth = 11;
          monthAgoYear = todayYear - 1;
        }
        // Use the minimum of today's day and the last day of the target month to avoid invalid dates
        const daysInTargetMonth = new Date(Date.UTC(monthAgoYear, monthAgoMonth + 1, 0)).getUTCDate();
        const monthAgoDay = Math.min(todayDay, daysInTargetMonth);
        startDate = new Date(Date.UTC(monthAgoYear, monthAgoMonth, monthAgoDay));
        endDate = new Date(todayUTC);
        break;
      case 'lastYear':
        // Calculate 1 year ago
        const yearAgo = new Date(Date.UTC(todayYear - 1, todayMonth, todayDay));
        startDate = yearAgo;
        endDate = new Date(todayUTC);
        break;
      case 'custom':
        if (!customStartDate || !customEndDate) {
          return null;
        }
        // Parse custom dates - they should already be in YYYY-MM-DD format from input
        const customStartParts = customStartDate.split('-');
        const customEndParts = customEndDate.split('-');
        if (customStartParts.length !== 3 || customEndParts.length !== 3) {
          return null;
        }
        startDate = new Date(Date.UTC(
          parseInt(customStartParts[0], 10),
          parseInt(customStartParts[1], 10) - 1, // Month is 0-indexed
          parseInt(customStartParts[2], 10)
        ));
        endDate = new Date(Date.UTC(
          parseInt(customEndParts[0], 10),
          parseInt(customEndParts[1], 10) - 1, // Month is 0-indexed
          parseInt(customEndParts[2], 10)
        ));
        // Validate dates
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          return null;
        }
        break;
      default:
        return null;
    }

      // Validate dates before formatting
      if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return null;
      }

    try {
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      
      // Validate formatted strings don't contain NaN
      if (startDateStr.includes('NaN') || endDateStr.includes('NaN') || !startDateStr || !endDateStr) {
        console.error('Invalid date strings generated:', { startDateStr, endDateStr, startDate, endDate });
        return null;
      }
      
      return {
        startDate: startDateStr,
        endDate: endDateStr
      };
    } catch (err) {
      console.error('Error formatting dates:', err, { startDate, endDate });
      return null;
    }
  };

  const handleExport = async () => {
    const dateRange = getDateRange(exportDateRange);
    
    if (!dateRange) {
      setToast({
        message: 'Please select a valid date range',
        type: 'error'
      });
      return;
    }

    try {
      setExporting(true);
      
      // Fetch JSON data
      const response = await authAxios.get(`${API_BASE_URL}/admin/visitor-sessions/export`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });

      // Create blob from JSON and trigger download
      const jsonString = JSON.stringify(response.data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `visitor-sessions-${dateRange.startDate}-to-${dateRange.endDate}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast({
        message: 'Export completed successfully!',
        type: 'success'
      });
      setShowExportModal(false);
    } catch (err) {
      console.error('Error exporting data:', err);
      console.error('Error response status:', err.response?.status);
      console.error('Error response data:', JSON.stringify(err.response?.data, null, 2));
      console.error('Error response headers:', err.response?.headers);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to export data';
      const errorDetails = err.response?.data?.details;
      if (errorDetails) {
        console.error('Error details:', JSON.stringify(errorDetails, null, 2));
      }
      setToast({
        message: errorMsg + (errorDetails ? ` (Check console for details)` : ''),
        type: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Generate calendar grid (using UTC to match backend)
  const generateCalendarDays = () => {
    const year = currentMonth.getUTCFullYear();
    const month = currentMonth.getUTCMonth();
    
    // First day of month (UTC)
    const firstDay = new Date(Date.UTC(year, month, 1));
    const firstDayOfWeek = firstDay.getUTCDay(); // 0 = Sunday, 6 = Saturday
    
    // Last day of month (UTC)
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDay.getUTCDate();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month (using UTC)
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(year, month, day));
      const dateStr = formatDateForAPI(date);
      const raw = calendar[dateStr];
      const data = typeof raw === 'object' && raw !== null
        ? { count: raw.count, bounceRate: raw.bounceRate, avgDurationSeconds: raw.avgDurationSeconds, designLoveRate: raw.designLoveRate, productClickRate: raw.productClickRate }
        : { count: raw || 0, bounceRate: null, avgDurationSeconds: null, designLoveRate: null, productClickRate: null };
      days.push({ date, dateStr, visitorCount: data.count, bounceRate: data.bounceRate, avgDurationSeconds: data.avgDurationSeconds, designLoveRate: data.designLoveRate, productClickRate: data.productClickRate });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  // Use UTC for today to match backend
  const today = new Date();
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const todayStr = formatDateForAPI(todayUTC);

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />
        <AdminTabs />
        <MarketingLinks />
        <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Visitor Activity</h1>
            <div className="flex items-center gap-2">
              {/* Export Button */}
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Data
              </button>
              
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 mr-4 border-r border-gray-300 pr-4">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    viewMode === 'calendar'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Calendar
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    viewMode === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Sessions
                </button>
              </div>
              
              {viewMode === 'calendar' && (
                <>
                  <button
                    onClick={handlePrevMonth}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={handleToday}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={handleNextMonth}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Next →
                  </button>
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading calendar...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          ) : viewMode === 'calendar' ? (
            <>
              <div className="mb-4">
                <h2 className="text-2xl font-semibold text-gray-800">{monthName}</h2>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2 mb-4">
                {/* Day headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="aspect-square"></div>;
                  }

                  const isToday = day.dateStr === todayStr;
                  const hasVisitors = day.visitorCount > 0;
                  const isPast = day.date < todayUTC && !isToday;
                  const tooltipParts = [];
                  if (hasVisitors) tooltipParts.push(`${day.visitorCount} visitor${day.visitorCount === 1 ? '' : 's'}`);
                  if (day.bounceRate != null) tooltipParts.push(`Bounce rate: ${day.bounceRate}%`);
                  if (day.avgDurationSeconds != null) tooltipParts.push(`Avg duration: ${formatDuration(day.avgDurationSeconds)}`);
                  if (day.designLoveRate != null) tooltipParts.push(`Design love: ${day.designLoveRate}%`);
                  if (day.productClickRate != null) tooltipParts.push(`Product click: ${day.productClickRate}%`);
                  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(' • ') : undefined;

                  return (
                    <button
                      key={day.dateStr}
                      onClick={() => handleDateClick(day.date)}
                      title={tooltip}
                      className={`
                        aspect-square p-2 rounded-lg border-2 transition-all
                        ${isToday 
                          ? 'border-blue-500 bg-blue-50 font-bold' 
                          : hasVisitors 
                            ? 'border-green-300 bg-green-50 hover:bg-green-100 cursor-pointer' 
                            : isPast
                              ? 'border-gray-200 bg-gray-50 text-gray-400'
                              : 'border-gray-200 bg-white hover:bg-gray-50 cursor-pointer'
                        }
                      `}
                    >
                      <div className="text-sm font-medium">{day.date.getUTCDate()}</div>
                      {hasVisitors && (
                        <div className="text-xs mt-1 text-green-700 font-semibold space-y-0.5">
                          <div>{day.visitorCount}</div>
                          {(day.bounceRate != null || day.avgDurationSeconds != null) && (
                            <div className="text-green-600 font-normal">
                              {[day.bounceRate != null && `${day.bounceRate}%`, day.avgDurationSeconds != null && formatDuration(day.avgDurationSeconds)].filter(Boolean).join(' • ')}
                            </div>
                          )}
                          {(day.designLoveRate != null || day.productClickRate != null) && (
                            <div className="text-green-600 font-normal">
                              {[day.designLoveRate != null && `♥${day.designLoveRate}%`, day.productClickRate != null && `${day.productClickRate}%`].filter(Boolean).join(' • ')}
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 bg-blue-50 rounded"></div>
                    <span>Today</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-green-300 bg-green-50 rounded"></div>
                    <span>Has visitors (click to view, hover for details)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-200 bg-gray-50 rounded"></div>
                    <span>No visitors</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <h2 className="text-2xl font-semibold text-gray-800">All Sessions</h2>
                <p className="text-gray-600 mt-1">
                  Showing {allSessions.length} session{allSessions.length !== 1 ? 's' : ''} from the last 30 days
                </p>
              </div>

              {allSessions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No visitor sessions found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {allSessions.map((session, index) => (
                    <div
                      key={`${session.date}-${session.sessionId}-${index}`}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <h3 className="font-semibold text-gray-900">
                              {session.sessionId.substring(0, 24)}...
                            </h3>
                            <span className="text-sm text-blue-600 font-medium">
                              {session.date}
                            </span>
                            <span className="text-sm text-gray-500">
                              {session.activityCount} {session.activityCount === 1 ? 'page visit' : 'page visits'}
                            </span>
                            <span className="text-sm text-gray-500">
                              Duration: {formatDuration(session.duration)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Started:</span> {formatDateTime(session.startTime)}
                            </div>
                            <div>
                              <span className="font-medium">Last Activity:</span> {formatDateTime(session.lastActivity)}
                            </div>
                            <div>
                              <span className="font-medium">IP Address:</span> {session.ipAddress}
                            </div>
                            <div>
                              <span className="font-medium">User Agent:</span>{' '}
                              <span className="text-xs">{session.userAgent.substring(0, 50)}...</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/visitor-activity/${session.date}`)}
                          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          View Day
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Export Visitor Data</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date Range
              </label>
              <select
                value={exportDateRange}
                onChange={(e) => setExportDateRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="lastWeek">Last Week</option>
                <option value="lastMonth">Last Month</option>
                <option value="lastYear">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {exportDateRange === 'custom' && (
              <div className="mb-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setExportDateRange('lastWeek');
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisitorActivityPage;

